import React, { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { FaRobot, FaUser } from "react-icons/fa";
import {
  getTicketDetails,
  replyToTicket,
  resolveTicket,
  closeTicket,
} from "../../../services/supportApi";
import { useToast } from "../../../mesageToast/ToastContainer";

const renderInlineMarkdown = (text) => {
  const parts = String(text ?? "").split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);

  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }

    if (part.startsWith("*") && part.endsWith("*")) {
      return <em key={index}>{part.slice(1, -1)}</em>;
    }

    return part;
  });
};

const BotMarkdownPreview = ({ text }) => {
  const blocks = String(text ?? "")
    .replace(/\r\n/g, "\n")
    .split(/\n{2,}|\n/g)
    .map((block) => block.trim())
    .filter(Boolean);

  return (
    <div className="markdown-preview">
      {blocks.map((block, index) => {
        const bulletMatch = block.match(/^[-*]\s+(.+)$/);

        if (bulletMatch) {
          return (
            <ul key={index}>
              <li>{renderInlineMarkdown(bulletMatch[1])}</li>
            </ul>
          );
        }

        return <p key={index}>{renderInlineMarkdown(block)}</p>;
      })}
    </div>
  );
};

const getSenderLabel = (sender) => {
  if (sender === "agent") return "You";
  if (sender === "bot") return "AI Bot";
  if (sender === "system") return "System";
  return "Customer";
};

const getSenderIcon = (sender) => {
  if (sender === "bot") return <FaRobot />;
  if (sender === "customer" || sender === "agent") return <FaUser />;
  return null;
};

const TicketDetail = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const addToast = useToast();
  const queryClient = useQueryClient();
  const messagesEndRef = useRef(null);

  const [replyMessage, setReplyMessage] = useState("");
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [note, setNote] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["support-ticket", ticketId],
    queryFn: () => getTicketDetails(ticketId),
  });

  const replyMutation = useMutation({
    mutationFn: (msg) => replyToTicket(ticketId, msg),
    onSuccess: () => {
      setReplyMessage("");
      queryClient.invalidateQueries(["support-ticket", ticketId]);
    },
    onError: () => {
      if (addToast) addToast("Error", "Failed to send reply", "error");
    },
  });

  const resolveMutation = useMutation({
    mutationFn: (resNote) => resolveTicket(ticketId, resNote),
    onSuccess: () => {
      if (addToast) addToast("Success", "Ticket resolved", "success");
      setShowResolveModal(false);
      queryClient.invalidateQueries(["support-ticket", ticketId]);
      queryClient.invalidateQueries(["support-escalations"]);
      navigate("/BOD/support-escalations/my-tickets");
    },
  });

  const closeMutation = useMutation({
    mutationFn: () => closeTicket(ticketId),
    onSuccess: () => {
      if (addToast) addToast("Success", "Ticket closed", "success");
      queryClient.invalidateQueries(["support-ticket", ticketId]);
      queryClient.invalidateQueries(["support-escalations"]);
      navigate("/BOD/support-escalations/my-tickets");
    },
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [data]);

  const ticket = data?.data?.ticket || {};
  const isClosedOrResolved =
    ticket.status === "resolved" || ticket.status === "closed";

  if (isLoading) return <p>Loading ticket details...</p>;

  return (
    <div className="ticket-detail">
      <div className="ticket-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <h2>Ticket: {ticket.ticketId}</h2>
        <span className={`status-badge ${ticket.status}`}>{ticket.status}</span>

        {!isClosedOrResolved && (
          <div className="actions">
            <button
              className="resolve-btn"
              onClick={() => setShowResolveModal(true)}
            >
              Resolve Issue
            </button>
            <button
              className="close-btn"
              onClick={() => closeMutation.mutate()}
            >
              Close Ticket
            </button>
          </div>
        )}
      </div>

      <div className="chat-container">
        <div className="messages">
          {ticket.conversationId?.messages?.map((msg, idx) => (
            <div key={idx} className={`message ${msg.sender}`}>
              {getSenderIcon(msg.sender) && (
                <span className="message-avatar" aria-hidden="true">
                  {getSenderIcon(msg.sender)}
                </span>
              )}
              <strong>
                {getSenderLabel(msg.sender)}:
              </strong>
              {msg.sender === "bot" ? (
                <BotMarkdownPreview text={msg.message} />
              ) : (
                <p>{msg.message}</p>
              )}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {!isClosedOrResolved && (
          <div className="chat-input">
            <textarea
              value={replyMessage}
              onChange={(e) => setReplyMessage(e.target.value)}
              placeholder="Type a message..."
              disabled={replyMutation.isPending}
            />
            <button
              onClick={() => replyMutation.mutate(replyMessage)}
              disabled={!replyMessage.trim() || replyMutation.isPending}
            >
              Send
            </button>
          </div>
        )}
      </div>

      {showResolveModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Resolve Ticket</h3>
            <textarea
              placeholder="Enter resolution note..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <div className="modal-actions">
              <button onClick={() => setShowResolveModal(false)}>Cancel</button>
              <button onClick={() => resolveMutation.mutate(note)}>
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketDetail;
